import { COLORS } from '@/utils/colors';
import React from 'react';

interface Column {
  label: string;
  key: string;
  className?: string;
}

interface TableProps {
  columns: Column[];
  data: Record<string, any>[];
  renderRow?: (item: Record<string, any>, index: number) => React.ReactNode;
  emptyMessage?: React.ReactNode;
  className?: string;
  // Pagination
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  totalRecords?: number;
  pageSize?: number;
  onRowClick?: (item: Record<string, any>) => void;
}

function Table({
  columns,
  data,
  renderRow,
  emptyMessage = 'No data to show',
  className = '',
  currentPage,
  totalPages,
  onPageChange,
  totalRecords,
  pageSize,
  onRowClick,
}: TableProps) {
  // Fallbacks for pagination if not provided
  const safeCurrentPage = currentPage || 1;
  const safeTotalPages = totalPages || 1;
  const safeTotalRecords = typeof totalRecords === 'number' ? totalRecords : data.length;
  return (
    <div
      style={{ backgroundColor: COLORS.white }}
      className={` rounded-lg overflow-hidden hide-scrollbar  flex flex-col ${className} max-h-[600px]`}>
      {/* Scrollable Table Body Container */}
      <div className="flex-1 overflow-y-auto hide-scrollbar">
        <table className="w-full">
          {/* Sticky Header */}
          <thead
            style={{ backgroundColor: COLORS.primary }}
            className="sticky top-0 z-10  ">
            <tr className="text-left">
              {columns.map((col) => (
                <th
                  style={{ color: COLORS.black }}
                  key={col.key}
                  className={`p-4 text-sm font-medium  ${col.className || ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, idx) =>
                renderRow ? (
                  <React.Fragment key={item.id || idx}>{renderRow(item, idx)}</React.Fragment>
                ) : (
                  <tr key={item.id || idx}
                    style={{ borderColor: COLORS.border, cursor: onRowClick ? 'pointer' : undefined }}
                    className={`border-t ${onRowClick ? 'hover:bg-gray-50 transition-colors' : ''}`}
                    onClick={() => onRowClick && onRowClick(item)}>
                    {columns.map((col) => (
                      <td
                        style={{ color: COLORS.black }}
                        key={col.key} className="p-4  text-sm ">
                        {item[col.key]}
                      </td>
                    ))}
                  </tr>
                )
              )
            ) : (
              <tr
                style={{ borderColor: COLORS.border }}
                className="border-t ">
                <td
                  style={{ color: COLORS.black }}
                  colSpan={columns.length} className="p-8 text-center ">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sticky Pagination Footer */}
      <div
        style={{ borderColor: COLORS.border, backgroundColor: COLORS.white }}
        className="flex-none flex justify-center items-center border-t  px-4 md:px-6 py-4 gap-2 flex-wrap sticky bottom-0 z-10">
        <button
          onClick={() => onPageChange && onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1 || !onPageChange}
          style={{ backgroundColor: COLORS.lightGray, color: COLORS.arrowGray }}
          className="px-2 py-1 rounded text-sm font-medium transition-colors disabled:opacity-50"
        >
          {'<'}
        </button>
        {Array.from({ length: safeTotalPages }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => onPageChange && onPageChange(i + 1)}
            style={{
              backgroundColor: safeCurrentPage === i + 1 ? COLORS.orange : "transparent",
              color: safeCurrentPage === i + 1 ? COLORS.white : COLORS.black,
            }}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors 
              
              `}
            disabled={safeCurrentPage === i + 1 || !onPageChange}
          >
            {i + 1}
          </button>
        ))}
        <button
          onClick={() => onPageChange && onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages || !onPageChange}
          style={{ backgroundColor: COLORS.lightGray, color: COLORS.arrowGray }}
          className="px-2 py-1 rounded text-sm font-medium transition-colors disabled:opacity-50"
        >
          {'>'}
        </button>
        <span
          style={{ color: COLORS.black }}
          className=" text-sm ml-4">
          Page {safeCurrentPage} of {safeTotalPages} (Total: {safeTotalRecords})
        </span>
      </div>
    </div>
  );
}

export default Table; 
